/**
 * Meta Pixel — Pagali (pagali.cv)
 * Pixel ID: 27320972990868937
 *
 * Eventos só são disparados APÓS consentimento do utilizador.
 * Se não houver consentimento, ficam em fila até grant().
 *
 * Compliance: nunca usar "banco", "garantido", "sem risco" em content_name.
 */

export const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID ?? '27320972990868937';

/** Base da API Pagali. Sem ela os eventos só saem pelo browser. */
const API_BASE = (process.env.REACT_APP_API_URL ?? '').replace(/\/+$/, '');

const COOKIE_FBP = '_fbp';
const COOKIE_FBC = '_fbc';
const LANDING_KEY = 'pagali_landing_url';
const FBC_MAX_AGE_DAYS = 90;

/** Eventos que o endpoint público aceita. Manter alinhado com meta-capi.ts. */
const SERVER_EVENTS = new Set([
  'PageView', 'ViewContent', 'Search', 'AddToCart',
  'InitiateCheckout', 'Lead', 'CompleteRegistration', 'Contact',
]);

export type StandardEvent =
  | 'PageView'
  | 'ViewContent'
  | 'Search'
  | 'Lead'
  | 'CompleteRegistration'
  | 'InitiateCheckout'
  | 'AddPaymentInfo'
  | 'Purchase'
  | 'Contact'
  | 'SubmitApplication';

export interface EventParameters {
  content_name?: string;
  content_category?: string;
  content_ids?: string[];
  content_type?: string;
  value?: number;
  currency?: string;
  num_items?: number;
  status?: string;
  [key: string]: unknown;
}

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue?: unknown[];
      loaded?: boolean;
      version?: string;
      push?: (...args: unknown[]) => void;
    };
    _fbq?: typeof window.fbq;
  }
}

// ─── Estado interno ───────────────────────────────────────────────────────────

let consentGranted = false;
let scriptLoaded = false;
const pendingEvents: Array<() => void> = [];

/** Utilizador autenticado, para correspondência avançada e `external_id`. */
let identity: { externalId?: string; email?: string; phone?: string } = {};

// ─── Cookies e atribuição ─────────────────────────────────────────────────────

function readCookie(name: string): string | null {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const hit = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.slice(name.length + 1)) : null;
}

function writeCookie(name: string, value: string, maxAgeDays: number): void {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${name}=${encodeURIComponent(value)}` +
    `; Max-Age=${Math.floor(maxAgeDays * 24 * 60 * 60)}` +
    `; Path=/; SameSite=Lax${secure}`;
}

/**
 * Converte `?fbclid=` no cookie `_fbc`.
 *
 * O Pixel faz isto sozinho, mas só depois de carregar — e aqui ele só carrega
 * após consentimento. Se o utilizador for redireccionado a meio (o que já
 * acontece no fluxo de registo), a atribuição perde-se e a conversão aparece
 * como tráfego directo. Daí a salvaguarda explícita.
 */
export function captureClickId(): void {
  if (typeof window === 'undefined') return;

  try {
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    if (fbclid && !readCookie(COOKIE_FBC)) {
      // Formato obrigatório: fb.<subdomain_index>.<timestamp_ms>.<fbclid>
      writeCookie(COOKIE_FBC, `fb.1.${Date.now()}.${fbclid}`, FBC_MAX_AGE_DAYS);
    }
  } catch { /* URL malformado não deve partir a página */ }

  try {
    if (!sessionStorage.getItem(LANDING_KEY)) {
      sessionStorage.setItem(LANDING_KEY, window.location.href);
    }
  } catch { /* sessionStorage bloqueado */ }
}

export interface Attribution {
  fbp: string | null;
  fbc: string | null;
  landingUrl: string | null;
}

/**
 * Cookies de atribuição para enviar com o formulário de registo.
 * Gravados em `pg_user_attribution`, são o que liga os eventos de servidor
 * (KYC aprovado, primeira transacção) ao clique original no anúncio.
 */
export function getAttribution(): Attribution {
  let landingUrl: string | null = null;
  try { landingUrl = sessionStorage.getItem(LANDING_KEY); } catch { /* bloqueado */ }
  return {
    fbp: readCookie(COOKIE_FBP),
    fbc: readCookie(COOKIE_FBC),
    landingUrl,
  };
}

// ─── Conversions API ──────────────────────────────────────────────────────────

function newEventId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback para contextos sem randomUUID. Não precisa de ser criptográfico,
  // só de não colidir dentro da janela de deduplicação do Meta.
  const b = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => (x + 0x100).toString(16).slice(1));
  return [
    h.slice(0, 4).join(''), h.slice(4, 6).join(''), h.slice(6, 8).join(''),
    h.slice(8, 10).join(''), h.slice(10, 16).join(''),
  ].join('-');
}

/**
 * Espelha o evento no servidor com o MESMO `event_id`.
 *
 * É o mesmo evento a chegar por dois caminhos: o browser (rápido, perde-se com
 * bloqueadores) e o servidor (fiável). O `event_id` partilhado é o que faz o
 * Meta contar uma vez em vez de duas — sem ele o CPA aparenta ser metade do
 * real e todas as decisões de orçamento saem erradas.
 */
function mirrorToCapi(
  eventName: string,
  eventId: string,
  params?: EventParameters,
): void {
  if (!API_BASE || !SERVER_EVENTS.has(eventName)) return;

  // custom_data: só escalares. Nunca IBAN, PAN, referência bancária ou
  // qualquer dado de KYC — só montante e moeda.
  const custom: Record<string, string | number> = {};
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (k === 'external_id') continue;
      if (typeof v === 'string' || typeof v === 'number') custom[k] = v;
    }
  }

  try {
    fetch(`${API_BASE}/v1/public/meta/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventName,
        eventId,
        eventSourceUrl: window.location.href,
        fbp: readCookie(COOKIE_FBP),
        fbc: readCookie(COOKIE_FBC),
        user: Object.keys(identity).length ? identity : undefined,
        custom: Object.keys(custom).length ? custom : undefined,
      }),
      // Sobrevive à navegação: sem isto perdem-se os eventos disparados
      // mesmo antes de o utilizador sair da página
      keepalive: true,
    }).catch(() => { /* telemetria nunca parte a experiência */ });
  } catch { /* fetch indisponível */ }
}

// ─── API pública ──────────────────────────────────────────────────────────────

export const metaPixel = {
  grantConsent(): void {
    consentGranted = true;
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('consent', 'grant');
    }
    while (pendingEvents.length > 0) {
      try { pendingEvents.shift()?.(); } catch { /* silencia */ }
    }
  },

  revokeConsent(): void {
    consentGranted = false;
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('consent', 'revoke');
    }
  },

  markLoaded(): void {
    scriptLoaded = true;
  },

  isReady(): boolean {
    return scriptLoaded && consentGranted && typeof window !== 'undefined' && !!window.fbq;
  },

  /**
   * Liga o utilizador autenticado ao Pixel e à CAPI.
   *
   * O `external_id` tem de ir em `user_data`, não em `custom_data` — no
   * segundo sítio o Meta ignora-o para efeitos de correspondência. É também
   * o que permite reconciliar a mesma pessoa entre a web e a app.
   */
  identify(user: { externalId?: string; email?: string; phone?: string }): void {
    identity = { ...identity, ...user };
    const fire = () => {
      if (typeof window === 'undefined' || !window.fbq) return;
      // Re-inicializar com dados de correspondência avançada é a forma
      // documentada de os actualizar depois do init original.
      window.fbq('init', META_PIXEL_ID, {
        ...(identity.externalId ? { external_id: identity.externalId } : {}),
        ...(identity.email ? { em: identity.email } : {}),
        ...(identity.phone ? { ph: identity.phone } : {}),
      });
    };
    this.isReady() ? fire() : pendingEvents.push(fire);
  },

  /** Esquecer o utilizador. Chamar no logout e ao apagar a conta. */
  clearIdentity(): void {
    identity = {};
  },

  /**
   * @param eventId Passar um ID determinístico quando o MESMO evento também
   *   sai do servidor (ver `deterministicEventId`). Sem isso, browser e
   *   servidor produzem IDs diferentes, o Meta não deduplica e a conversão
   *   conta a dobrar.
   */
  track(event: StandardEvent, params?: EventParameters, eventId?: string): void {
    const id = eventId ?? newEventId();
    const fire = () => {
      if (typeof window === 'undefined' || !window.fbq) return;
      window.fbq('track', event, params ?? {}, { eventID: id });
    };
    // O espelho de servidor segue o mesmo destino do evento de browser:
    // se está em fila à espera de consentimento, também espera.
    const mirror = () => mirrorToCapi(event, id, params);
    if (this.isReady()) { fire(); mirror(); }
    else { pendingEvents.push(fire); pendingEvents.push(mirror); }
  },

  trackCustom(name: string, params?: EventParameters, eventId?: string): void {
    const id = eventId ?? newEventId();
    const fire = () => {
      if (typeof window === 'undefined' || !window.fbq) return;
      window.fbq('trackCustom', name, params ?? {}, { eventID: id });
    };
    const mirror = () => mirrorToCapi(name, id, params);
    if (this.isReady()) { fire(); mirror(); }
    else { pendingEvents.push(fire); pendingEvents.push(mirror); }
  },

  pageView(): void {
    this.track('PageView');
  },
};

// ─── Atalhos de domínio Pagali ────────────────────────────────────────────────

/**
 * Mesmo ID que o servidor gera em `meta-capi.ts` para o mesmo facto.
 * É o que faz o Meta tratar os dois envios como um só evento.
 */
export function deterministicEventId(eventName: string, userId: string): string {
  return `${eventName}_${userId}`;
}

export const pagaliEvents = {
  registerStarted(): void {
    metaPixel.track('Lead', { content_name: 'Registo Pagali iniciado' });
  },

  registerCompleted(userId?: string): void {
    // O servidor envia o mesmo evento a partir do funil de activação.
    // Com o userId, os dois envios partilham event_id e deduplicam.
    if (userId) metaPixel.identify({ externalId: userId });
    metaPixel.track(
      'CompleteRegistration',
      { content_name: 'Registo Pagali completo', status: 'kyc_pending' },
      userId ? deterministicEventId('CompleteRegistration', userId) : undefined,
    );
  },

  /**
   * A aprovação do KYC acontece no back-office, não no browser — a fonte de
   * verdade é o trigger do funil, que envia `KYC_Aprovado` pela CAPI.
   * Este atalho existe só para o caso de a app querer refletir o facto
   * localmente; usa o mesmo nome e o mesmo event_id para não duplicar.
   */
  kycCompleted(userId?: string): void {
    if (userId) metaPixel.identify({ externalId: userId });
    metaPixel.trackCustom(
      'KYC_Aprovado',
      { content_name: 'KYC Pagali aprovado', status: 'kyc_1_approved' },
      userId ? deterministicEventId('KYC_Aprovado', userId) : undefined,
    );
  },

  initiateCheckout(valueCve: number): void {
    metaPixel.track('InitiateCheckout', { value: valueCve, currency: 'CVE' });
  },

  purchaseCompleted(valueCve: number, orderId: string): void {
    metaPixel.track('Purchase', {
      value: valueCve,
      currency: 'CVE',
      content_ids: [orderId],
      content_type: 'product',
    });
  },
};

// Compatibilidade com código existente que usa pixel.track directamente
export const pixel = {
  track: (event: string, data?: Record<string, unknown>) =>
    metaPixel.track(event as StandardEvent, data),
};
